import type { PayloadAction } from '@reduxjs/toolkit';

import { asyncThunkCreator, buildCreateSlice } from '@reduxjs/toolkit';
import { toast } from 'sonner';

import { selectCartProduct } from '@screens/Cart/api/selectCartProduct/selectCartProduct';
import { NOTIFICATION_PRODUCT_ADDED_TO_CART } from '@shared/constants/text';
import { HttpCodes, HttpStatuses } from '@shared/types/enums/http.enum';
import { ProductCardType } from '@shared/types/product';
import { CartNotification } from '@shared/ui/CartNotification';
import { getCookiesProductsForUser, setCookiesProductsForUser } from '@shared/utils/cookiesUtils/cookiesUtils';
import { openAlert } from '@widgets/AlertModal';
import { ProductsForUser } from '@widgets/Auth/model/types/productsForUser';

import {
  addProductToCart,
  CartProductList,
  CartStateType,
  removeProductFromCart,
  SelectCartProductsRequest
} from '../../';

const createAppSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator }
});

const initialState: CartStateType = {
  productIds: []
};

export const cartSlice = createAppSlice({
  name: 'cart',
  initialState,
  reducers: (create) => ({
    updateCartList: create.reducer((state, action: PayloadAction<Array<{ productId: string; quantity: string }>>) => {
      state.productIds = action.payload?.map(({ productId }) => productId) || [];
    }),
    selectCartProductThunk: create.asyncThunk(async (selectedProducts: SelectCartProductsRequest, thunkAPI) => {
      const res = await selectCartProduct(selectedProducts);

      switch (res.code) {
        case HttpCodes.UNAUTHORIZED: {
          const bodyReq: Record<string, { productId: string; selected: boolean }> = selectedProducts.reduce(
            (acc, item) => ({ ...acc, [item.productId]: item }),
            {}
          );

          const cookiesCartCount = getCookiesProductsForUser<ProductsForUser['cartCount']>('cartCount');

          const cartCount = cookiesCartCount.map((item) => ({
            ...item,
            selected: bodyReq[item?.productId] ? bodyReq[item?.productId]?.selected : item.selected
          }));

          const cartCountObj: CartProductList['products'] = cartCount.reduce(
            (acc, item) => ({ ...acc, [item.productId]: item }),
            {}
          );

          setCookiesProductsForUser({ cartCount });

          return {
            data: {
              products: cartCountObj
            }
          };
        }
        case HttpCodes.CREATED: {
          return res;
        }
        default: {
          thunkAPI.dispatch(
            openAlert({
              data: {
                title: 'Ошибка',
                text: 'Попробуйте позже'
              },
              variant: HttpStatuses.ERROR
            })
          );
          return thunkAPI.rejectWithValue(HttpStatuses.ERROR);
        }
      }
    }),
    changeQuantityProductCartThunk: create.asyncThunk(
      async ({ id, quantity }: { id: string; quantity: number }, thunkAPI) => {
        const res = await addProductToCart([{ productId: id, quantity }]);

        switch (res.code) {
          case HttpCodes.UNAUTHORIZED: {
            const cookiesCartCount = getCookiesProductsForUser<ProductsForUser['cartCount']>('cartCount');

            const cartCount = cookiesCartCount.map((item) =>
              item.productId === id ? { ...item, quantity: String(quantity) } : item
            );

            setCookiesProductsForUser({
              cartCount
            });

            break;
          }
          case HttpCodes.CREATED: {
            break;
          }
          default: {
            thunkAPI.dispatch(
              openAlert({
                data: {
                  title: 'Ошибка',
                  text: 'Попробуйте позже'
                },
                variant: HttpStatuses.ERROR
              })
            );
            return thunkAPI.rejectWithValue(HttpStatuses.ERROR);
          }
        }
      }
    ),
    removeProductsFromCartThunk: create.asyncThunk(
      async (ids: string[], thunkAPI) => {
        const res = await removeProductFromCart(ids);

        switch (res.code) {
          case HttpCodes.UNAUTHORIZED: {
            const cookiesCartCount = getCookiesProductsForUser<ProductsForUser['cartCount']>('cartCount');

            const cartCount = cookiesCartCount.filter(({ productId }) => !ids?.includes(productId));

            setCookiesProductsForUser({
              cartCount
            });

            break;
          }
          case HttpCodes.OK: {
            break;
          }
          default: {
            thunkAPI.dispatch(
              openAlert({
                data: {
                  title: 'Ошибка',
                  text: 'Попробуйте позже'
                },
                variant: HttpStatuses.ERROR
              })
            );
            return thunkAPI.rejectWithValue(HttpStatuses.ERROR);
          }
        }

        return {
          ids
        };
      },
      {
        fulfilled: (state, { payload: { ids } }) => {
          state.productIds = state.productIds.filter((id) => !ids.includes(id));
        }
      }
    ),
    addProductToCartThunk: create.asyncThunk(
      async (product: ProductCardType, thunkAPI) => {
        const res = await addProductToCart([{ productId: product.id, quantity: product.quantity || 0 }]);

        switch (res.code) {
          case HttpCodes.UNAUTHORIZED: {
            const cookiesCartCount = getCookiesProductsForUser<ProductsForUser['cartCount']>('cartCount');

            const includeProduct = cookiesCartCount.find(({ productId }) => productId === product.id);

            const cartCount = includeProduct
              ? cookiesCartCount.map(({ productId, quantity }) => ({
                  productId,
                  quantity: quantity + includeProduct.quantity,
                  selected: true
                }))
              : [
                  { productId: product.id, quantity: String(product.quantity || 0), selected: true },
                  ...cookiesCartCount
                ];

            setCookiesProductsForUser({
              cartCount
            });

            break;
          }
          case HttpCodes.CREATED: {
            break;
          }
          default: {
            thunkAPI.dispatch(
              openAlert({
                data: {
                  title: 'Ошибка',
                  text: 'Попробуйте позже'
                },
                variant: HttpStatuses.ERROR
              })
            );
            return thunkAPI.rejectWithValue(HttpStatuses.ERROR);
          }
        }

        const imageUrl = product.images ? product.images[0].value : '';

        toast(CartNotification({ image: imageUrl, text: NOTIFICATION_PRODUCT_ADDED_TO_CART }), {
          duration: 3000,
          position: 'top-right'
        });

        return product.id;
      },
      {
        fulfilled: (state, action) => {
          state.productIds.push(action.payload);
        }
      }
    )
  })
});

export const {
  addProductToCartThunk,
  removeProductsFromCartThunk,
  selectCartProductThunk,
  changeQuantityProductCartThunk,
  updateCartList
} = cartSlice.actions;

export const cartReducer = cartSlice.reducer;
